import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BookAward, AwardWithBooks } from '@/types/awards';

export function useAwards(year?: number) {
  return useQuery({
    queryKey: ['awards', year],
    queryFn: async () => {
      let query = supabase
        .from('book_awards')
        .select('*')
        .order('shortlist_date', { ascending: true });

      if (year) {
        query = query.eq('year', year);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as BookAward[];
    },
  });
}

export function useAwardWithBooks(awardId: string) {
  return useQuery({
    queryKey: ['award', awardId, 'books'],
    queryFn: async () => {
      // Fetch award details
      const { data: award, error: awardError } = await supabase
        .from('book_awards')
        .select('*')
        .eq('id', awardId)
        .single();

      if (awardError) throw awardError;

      // Fetch shortlist books
      const { data: shortlistData, error: shortlistError } = await supabase
        .from('award_shortlist_books')
        .select(`
          id,
          position,
          book_id,
          books (
            id,
            title,
            authors,
            cover_url
          )
        `)
        .eq('award_id', awardId)
        .order('position', { ascending: true, nullsFirst: false });

      if (shortlistError) throw shortlistError;

      const shortlist_books = (shortlistData || []).map((item: any) => ({
        id: item.books.id,
        title: item.books.title,
        authors: item.books.authors || [],
        cover_url: item.books.cover_url,
        position: item.position,
      }));

      return {
        ...award,
        shortlist_books,
      } as AwardWithBooks;
    },
    enabled: !!awardId,
  });
}

export function useAwardsByMonth() {
  return useQuery({
    queryKey: ['awards', 'by-month'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('book_awards')
        .select(`
          *,
          award_shortlist_books (
            id,
            position,
            book_id,
            books (
              id,
              title,
              authors,
              cover_url
            )
          )
        `)
        .eq('year', 2024)
        .order('shortlist_date', { ascending: true });

      if (error) throw error;

      return (data || []).map((award: any) => ({
        ...award,
        shortlist_books: (award.award_shortlist_books || [])
          .map((item: any) => ({
            id: item.books?.id,
            title: item.books?.title,
            authors: item.books?.authors || [],
            cover_url: item.books?.cover_url,
            position: item.position,
          }))
          .filter((book: any) => book.id), // Filter out null books
      })) as AwardWithBooks[];
    },
  });
}
